import { useMemo } from 'react';
import { ThemeProvider } from 'styled-components';
import { themes } from '@electricjs/arc';

import { useQueryString } from '@/hooks/useQueryString';
import { ThemingParamKeys } from '@/types/queryParamKeys';
import { TRINET_LIGHT_THEME, TRINET_DARK_THEME } from '@/themes/trinetTheme';
import { ISOLVED1_THEME, ISOLVED2_THEME } from '@/themes/isolvedTheme';

export const EmbeddedThemeValues = {
  Trinet: 'trinet',
  TrinetDark: 'trinet_dark',
  Dark: 'dark',
  Isolved1: 'isolved1',
  Isolved2: 'isolved2',
} as const;

type EmbeddedThemeProviderProps = {
  children: React.ReactNode;
};

/**
 * Theme provider for all embedded pages
 *
 * Uses custom Arc themes that override only the primary color.
 * Arc components automatically use theme colors for consistent styling.
 *
 * Supported themes (via ?theme parameter):
 *   - default (no param) - Standard Electric theme
 *   - trinet (?theme=trinet) - Trinet theme
 *   - dark (?theme=dark) - Electric dark theme
 *   - isolved1 (?theme=isolved1) - isolved theme 1
 *   - isolved2 (?theme=isolved2) - isolved theme 2
 *
 * Examples:
 *   /embed/storefront - Uses default Electric theme
 *   /embed/storefront?theme=trinet - Uses Trinet custom theme
 *   /embed/ship-laptop?theme=dark - Uses Electric dark theme
 */
export const EmbeddedThemeProvider = ({
  children,
}: EmbeddedThemeProviderProps) => {
  const queryParams = useQueryString();
  const themeParam = queryParams.get(ThemingParamKeys.Theme);

  // Select Arc theme based on query parameter
  const arcTheme = useMemo(() => {
    if (themeParam === EmbeddedThemeValues.TrinetDark) {
      return TRINET_DARK_THEME;
    } else if (themeParam === EmbeddedThemeValues.Trinet) {
      return TRINET_LIGHT_THEME;
    } else if (themeParam === EmbeddedThemeValues.Isolved1) {
      return ISOLVED1_THEME;
    } else if (themeParam === EmbeddedThemeValues.Isolved2) {
      return ISOLVED2_THEME;
    } else if (themeParam === EmbeddedThemeValues.Dark) {
      return themes.ELECTRIC_DARK;
    }
    return themes.ELECTRIC;
  }, [themeParam]);

  return <ThemeProvider theme={arcTheme}>{children}</ThemeProvider>;
};
